import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, ArrowRight, ListPlus, Pencil, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Lists() {
  const { user } = useAuth();
  const [lists, setLists] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null); // { id, name }
  const [renameValue, setRenameValue] = useState("");
  const navigate = useNavigate();

  const fetchLists = async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from("user_lists")
      .select("*, list_contacts(count)")
      .eq("user_id", user.id)
      .order("name");
    if (error) { toast.error(error.message); return; }
    setLists(
      (data || []).map((l) => ({
        id: l.id,
        name: l.name,
        count: l.list_contacts[0]?.count ?? 0,
      }))
    );
  };

  useEffect(() => { fetchLists(); }, [user?.id]); // eslint-disable-line

  const onDelete = async (list) => {
    const { error } = await supabase
      .from("user_lists")
      .delete()
      .eq("id", list.id)
      .eq("user_id", user.id);
    if (error) { toast.error(error.message); return; }
    toast.success(`Deleted "${list.name}"`);
    setLists((prev) => (prev || []).filter((l) => l.id !== list.id));
  };

  const openRename = (list) => { setRenameTarget(list); setRenameValue(list.name); };
  const submitRename = async () => {
    if (!renameTarget) return;
    const trimmed = renameValue.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    if (trimmed === renameTarget.name) { setRenameTarget(null); return; }
    const { error } = await supabase
      .from("user_lists")
      .update({ name: trimmed })
      .eq("id", renameTarget.id)
      .eq("user_id", user.id);
    if (error) {
      toast.error(error.message.includes("duplicate") ? "You already have a list with that name" : error.message);
      return;
    }
    toast.success("List renamed");
    setLists((prev) => (prev || []).map((l) => l.id === renameTarget.id ? { ...l, name: trimmed } : l));
    setRenameTarget(null);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <div>
          <div className="eyebrow">Workspace</div>
          <h1 className="font-display text-2xl tracking-tight">Lists</h1>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => navigate("/research")} data-testid="back-to-research">
          Back to Research <ArrowRight className="h-4 w-4" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10">
        <div className="mx-auto w-full max-w-4xl space-y-3">
          {lists === null ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : lists.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-surface p-12 text-center">
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-accent text-accent-foreground">
                <ListPlus className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-xl tracking-tight">No lists yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Select rows on the Research page and choose <em>Add to list</em> to organize leads.
              </p>
              <Button className="mt-4" onClick={() => navigate("/research")} data-testid="empty-lists-go-research">
                Go to Research
              </Button>
            </div>
          ) : (
            lists.map((l) => (
              <div
                key={l.id}
                className="group flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-5 py-4 transition-colors hover:border-primary/60"
                data-testid={`list-row-${l.name}`}
              >
                <button
                  onClick={() => navigate(`/lists/${l.id}`)}
                  className="flex flex-1 items-center gap-3 text-left"
                  data-testid={`open-list-${l.name}`}
                >
                  <div className="grid h-9 w-9 place-items-center rounded-md bg-accent text-accent-foreground font-display">
                    {l.name[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {l.count} {l.count === 1 ? "lead" : "leads"}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="font-mono-bd mr-1">{l.count}</Badge>
                  <Button variant="ghost" size="icon" data-testid={`rename-list-${l.name}`} aria-label={`Rename ${l.name}`} onClick={() => openRename(l)}>
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`delete-list-${l.name}`} aria-label={`Delete ${l.name}`}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete list "{l.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes the list and all its memberships. The leads themselves are not deleted.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`cancel-delete-list-${l.name}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(l)} data-testid={`confirm-delete-list-${l.name}`}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <ChevronRight className="ml-1 h-4 w-4 text-muted-foreground/60" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={!!renameTarget} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename list</DialogTitle>
            <DialogDescription>Choose a new name for this list.</DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitRename()}
            autoFocus
            data-testid="rename-list-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={submitRename} data-testid="confirm-rename-list">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
